import React, { useEffect, useState } from "react";
import Topbar from "./Topbar";
import Loading from "../../common/Laoding";
import { useNavigate } from "react-router-dom";
import {
  Search,
  X,
  LayoutGrid,
  List,
  Star,
  Clock,
  Grid,
  BarChart3,
  TrendingUp,
  Settings,
  Layers,
  Sparkles,
  ArrowRight,
  Sun,
  Moon,
  Monitor,
  Check,
  TriangleAlert,
} from "lucide-react";

// Import all your PNG images
import pettycash from "../../assets/cashflow.png";
import fuelmanagement from "../../assets/fuel.png";
import Viewbookings from "../../assets/view.png";
import peakseasonicon from "../../assets/peakseason.png";
import reports from "../../assets/reports.png";
import bookingdashboard from "../../assets/bookingdashboard.png";
import vehiclerevenue from "../../assets/vehiclerevenue.png";
import fleet from "../../assets/fleet.png";
import partnerwithus from "../../assets/partner.png";
import subscription from "../../assets/subscription.png";
import fleetsdashboard from "../../assets/fleet-supervisor.png";
import vehiclesdashboard from "../../assets/vehicledashboard.png";
import issuesdashboard from "../../assets/issues.png";
import feedbacks from "../../assets/feedback.png";
import support from "../../assets/support.png";
import workorders from "../../assets/tasking.png";
import accounting from "../../assets/accounting.png";
import recoveryPending from "../../assets/recovery.png";
import kyv from "../../assets/kyv.png";
import fleetoperations from "../../assets/fleetoperations.png";
import clutchnotification from "../../assets/clutchreminder.png";
import Vehiclegroupsales from "../../assets/Vehiclegroupsales.png";
import bookinganalytics from "../../assets/bookinganalysis.png";
import fleetutilization from "../../assets/fleet-utilization.png";
import pettycashdistribution from "../../assets/pettycashdistribution.png";
import wheelalignment from "../../assets/wheelalignment.png";
import documentIcon from "../../assets/document.png";
import emi from "../../assets/interest-rate.png";
import userManageIcon from "../../assets/usermanage.png";
import adminIcon from "../../assets/admin.png";

// Category Images - Add your PNG paths here
import salesIcon from "../../assets/sales.png"; // {/* put img here */}
import fleetIcon from "../../assets/fleet1.png"; // {/* put img here */}
import bookingIcon from "../../assets/booking.png"; // {/* put img here */}
import backendIcon from "../../assets/backend.png"; // {/* put img here */}
import accountIcon from "../../assets/budgeting.png"; // {/* put img here */}
import otherIcon from "../../assets/other.png"; // {/* put img here */}
import UseOnlineStatus from "../../common/UseOnlineStatus";
import NoInternet from "../../common/NoInternet";

// Custom hook for theme management
const useTheme = () => {
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "system";
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme) return savedTheme;
    return "light";
  });

  useEffect(() => {
    if (typeof document === "undefined") return;

    const root = document.documentElement;
    if (!root) return;

    const applyTheme = (themeValue) => {
      try {
        root.classList.remove("dark", "light");

        if (themeValue === "system") {
          const systemPrefersDark = window.matchMedia(
            "(prefers-color-scheme: dark)",
          ).matches;
          root.classList.add(systemPrefersDark ? "dark" : "light");
        } else {
          root.classList.add(themeValue);
        }
      } catch (error) {
        console.warn("Error applying theme:", error);
      }
    };

    applyTheme(theme);

    try {
      localStorage.setItem("theme", theme);
    } catch (error) {
      console.warn("Error saving theme:", error);
    }

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => {
      if (theme === "system") {
        applyTheme("system");
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [theme]);

  return [theme, setTheme];
};

const Dashboard = () => {
  const navigate = useNavigate();
  const profile = JSON.parse(localStorage.getItem("profile"));
  const actions = JSON.parse(localStorage.getItem("catalogValues"))?.action;
  const rolesFromResponse = profile?.roles;
  const rolesFromCatalogValues = JSON.parse(
    localStorage.getItem("catalogValues"),
  )?.role?.inverted;
  const actionFromResponse = profile?.actions || [];
  const { isOnline, isSlowConnection } = UseOnlineStatus();
  const [isLoading, setIsLoading] = useState(true);
  const [filteredModules, setFilteredModules] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const [activeCategory, setActiveCategory] = useState("sales"); // Default to 'sales'
  const [favorites, setFavorites] = useState(() => {
    return JSON.parse(localStorage.getItem("favoriteDashboards") || "[]");
  });
  const [recentDashboards, setRecentDashboards] = useState(() => {
    return JSON.parse(localStorage.getItem("recentDashboards") || "[]");
  });
  const [hoveredModule, setHoveredModule] = useState(null);
  const [theme, setTheme] = useState("light");
  const [isThemeDropdownOpen, setIsThemeDropdownOpen] = useState(false);

  // Inside your Dashboard component, add state:
  const [isAIAssistantOpen, setIsAIAssistantOpen] = useState(false);

  // Category Images
  const categoryImages = {
    sales: salesIcon,
    fleet: fleetIcon,
    booking: bookingIcon,
    backend: backendIcon,
    others: otherIcon,
    accounts: accountIcon,
    admin: adminIcon,
  };

  // Define module configurations with their categories
  const moduleCardConfig = {
    // ===== SALES CATEGORY (6) =====
    "Vehicle Revenue Dashboard": {
      title: "Vehicle Revenue",
      image: vehiclerevenue,
      route: "/vehiclerevenue",
      value: "vehiclerevenue",
      category: "fleet",
      description: "Track vehicle revenue performance",
    },
    "View Reports Dashboard": {
      title: "Reports",
      image: reports,
      route: "/reports",
      value: "reports",
      category: "others",
      description: "Generate and view reports",
    },
    "View Sales Agent Dashboard": {
      title: "Sales Agent Dashboard",
      image: support,
      route: "/salesagentdashboard",
      value: "salesagentdashboard",
      category: "sales",
      description: "Sales agent performance metrics",
    },
    "View Segmented Sales Dashboard": {
      title: "Segmented Sales",
      image: Vehiclegroupsales,
      route: "/segmentedsales",
      value: "segmentedsales",
      category: "sales",
      description: "Segmented sales analysis",
    },
    "View Recovery Dashboard": {
      title: "Recovery Pending",
      image: recoveryPending,
      route: "/recoverypending",
      value: "recoverypending",
      category: "backend",
      description: "Track pending recoveries",
    },
    "View Booking Ledger Dashboard": {
      title: "Booking Ledger",
      image: accounting,
      route: "/bookingledger",
      value: "bookingledger",
      category: "accounts",
      description: "Booking financial records",
    },

    // ===== FLEET CATEGORY (8) =====
    "View Fleet Overview Dashboard": {
      title: "Fleet Dashboard",
      image: fleet,
      route: "/fleetdashboard",
      value: "fleetdashboard",
      category: "fleet",
      description: "Comprehensive fleet overview",
    },
    "View All Vehicles": { 
      title: "Vehicles Dashboard",
      image: vehiclesdashboard,
      route: "/vehiclesdashboard",
      value: "vehiclesdashboard",
      category: "fleet",
      description: "Complete vehicle management",
    },
    "View Fleet Operation Dashboard": {
      title: "Fleet Operations",
      image: fleetoperations,
      route: "/fleetoperation",
      value: "fleetoperation",
      category: "fleet",
      description: "Fleet operational analytics",
    },
    "Get Vehicle Details": {
      title: "Know Your Vehicle",
      image: kyv,
      route: "/knowyourvehicle",
      value: "knowyourvehicle",
      category: "fleet",
      description: "Vehicle information and details",
    },
    "Fleet Utilization": {
      title: "Fleet Utilization",
      image: fleetutilization,
      route: "/fleetutilization",
      value: "fleetutilization",
      category: "fleet",
      description: "Fleet utilization metrics",
    },
    "Vehicle EMI Dashboard": {
      title: "Vehicle EMI Dashboard",
      image: emi,
      route: "/vehicleemidashboard",
      value: "vehicleemidashboard",
      category: "fleet",
      description: "Vehicle EMI tracking",
    },
    "Vehicle Document Dashboard": {
      title: "Vehicle Document Dashboard",
      image: documentIcon,
      route: "/vehicledocumentdashboard",
      value: "vehicledocumentdashboard",
      category: "fleet",
      description: "Vehicle document management",
    },
    "View Clutch Notification Dashboard": {
      title: "Clutch Notifications",
      image: clutchnotification,
      route: "/clutchnotification",
      value: "clutchnotification",
      category: "fleet",
      description: "Clutch notification management",
    },

    // ===== BOOKING CATEGORY (5) =====
    "View All Booking": {
      title: "View Bookings",
      image: Viewbookings,
      route: "/viewallbookings",
      value: "viewallbookings",
      category: "booking",
      description: "View and manage all bookings",
    },
    // "Booking Dashboard": {
    //   title: "Booking Dashboard",
    //   image: bookingdashboard,
    //   route: "/bookingdashboard",
    //   value: "bookingdashboard",
    //   category: "booking",
    //   description: "Real-time booking analytics",
    // },
    "Booking Analytics": {
      title: "Booking Analytics",
      image: bookinganalytics,
      route: "/bookinganalytics",
      value: "bookinganalytics",
      category: "booking",
      description: "In-depth booking analytics",
    },
    "View Peak Season": {
      title: "Booking Configuration",
      image: peakseasonicon,
      route: "/peakseason",
      value: "peakseason",
      category: "booking",
      description: "Configure peak season settings",
    },
    "Wheel Alignment Dashboard": {
      title: "Wheel Alignment Dashboard",
      image: wheelalignment,
      route: "/wheelalignmentdashboard",
      value: "wheelalignmentdashboard",
      category: "fleet",
      description: "Wheel alignment monitoring",
    },

    // ===== BACKEND CATEGORY (8) =====
    "View All Fuel Logs": {
      title: "Fuel Management",
      image: fuelmanagement,
      route: "/fuelmanagement",
      value: "fuelmanagement",
      category: "fleet",
      description: "Monitor fuel consumption and logs",
    },
    "View All Petty Cash": {
      title: "Petty Cash",
      image: pettycash,
      route: "/pettycash",
      value: "pettycash",
      category: "fleet",
      description: "Manage and track petty cash expenses",
    },
    "Petty Cash Dashboard": {
      title: "Petty Cash Dashboard",
      image: pettycashdistribution,
      route: "/pettycashdashboard",
      value: "pettycashdashboard",
      category: "accounts",
      description: "Petty cash distribution tracking",
    },
    "View All Workorder": {
      title: "Work Order",
      image: workorders,
      route: "/workorder",
      value: "workorder",
      category: "fleet",
      description: "Manage work orders",
    },
    "View all Issues": {
      title: "View all Issues",
      image: issuesdashboard,
      route: "/viewallissues",
      value: "viewallissues",
      category: "fleet",
      description: "Track and resolve issues",
    },
    // "Assign Pickup And Delivery Agent": {
    //   title: "Delivery & Pickups Agent Dashboard",
    //   image: fleetsdashboard,
    //   route: "/deliverypickupsagentdashboard",
    //   value: "deliverypickupsagentdashboard",
    //   category: "fleet",
    //   description: "Manage delivery and pickup agents",
    // },
    "Partner With US": {
      title: "Partner With Us",
      image: partnerwithus,
      route: "/partnerwithus",
      value: "partnerwithus",
      category: "others",
      description: "Partner registration and management",
    },
    "View All Subscription Inquiry": {
      title: "Subscription Dashboard",
      image: subscription,
      route: "/subscriptiondashboard",
      value: "subscriptiondashboard",
      category: "others",
      description: "Manage subscription inquiries",
    },

    // ===== OTHER CATEGORY (2) =====
    "View All Customer": {
      title: "Customer Management",
      image: feedbacks,
      route: "/customermanagement",
      value: "customermanagement",
      category: "others",
      description: "Manage customer relationships",
    },
    "View Reports Dashboard": {
      title: "Reports",
      image: reports,
      route: "/reports",
      value: "reports",
      category: "others",
      description: "Generate and view reports",
    },

    // ===== ADMIN CATEGORY (2) =====
    // "View All Users": {
    //   title: "User Management",
    //   image: userManageIcon,
    //   route: "/usermanagement",
    //   value: "usermanagement",
    //   category: "admin",
    //   description: "Manage users, roles, and platform permissions",
    // },
  };

  const actionToModuleDirectMap = Object.keys(moduleCardConfig).reduce(
    (acc, moduleName) => {
      const normalized = moduleName.toLowerCase();
      acc[normalized] = moduleCardConfig[moduleName].value;
      return acc;
    },
    {},
  );

  const getModuleValueFromActionName = (actionName) => {
    if (!actionName) return null;
    const normalized = actionName.toLowerCase().trim();
    return actionToModuleDirectMap[normalized] || null;
  };

  const getDashboardAccess = () => {
    const privilegedRoles = [
      rolesFromCatalogValues?.superAdmin,
      rolesFromCatalogValues?.tenantAdmin,
      rolesFromCatalogValues?.admin,
    ].filter(Boolean);
    const privilegedRolesForSales = [
      rolesFromCatalogValues?.accountManager,
    ].filter(Boolean);

    const userRoles = (rolesFromResponse || []).map(String);
    const hasFullAccess = userRoles.some((roleId) =>
      privilegedRoles.includes(roleId),
    );
    const hasFullAccessSalesDashboardAccess = userRoles.some((r) =>
      privilegedRolesForSales.includes(String(r)),
    );

    let allowedModules;

    if (hasFullAccess) {
      allowedModules = Object.values(moduleCardConfig);
    } else if (userRoles.includes(rolesFromCatalogValues?.franchiseAdmin)) {
      const actionNames = (actionFromResponse || []).map(
        (actionKey) => actions?.[actionKey],
      );

      const uniqueActions = [...new Set(actionNames)];

      const matchedModuleValues = uniqueActions
        .map(getModuleValueFromActionName)
        .filter(Boolean);

      allowedModules = matchedModuleValues
        .map((value) =>
          Object.values(moduleCardConfig).find((m) => m.value === value),
        )
        .filter(Boolean);
    } else {
      const actionNames = (actionFromResponse || []).map(
        (actionKey) => actions?.[actionKey],
      );

      const uniqueActions = [...new Set(actionNames)];

      const matchedModuleValues = uniqueActions
        .map(getModuleValueFromActionName)
        .filter(Boolean);

      allowedModules = matchedModuleValues
        .map((value) =>
          Object.values(moduleCardConfig).find((m) => m.value === value),
        )
        .filter(Boolean);
    }

    if (hasFullAccessSalesDashboardAccess) {
      const salesModule = Object.values(moduleCardConfig).find(
        (m) => m.value === "salesagentdashboard",
      );

      const alreadyExists = allowedModules.some(
        (m) => m.value === "salesagentdashboard",
      );

      if (salesModule && !alreadyExists) {
        allowedModules.push(salesModule);
      }
    }

    setFilteredModules(allowedModules);
  };

  useEffect(() => {
    getDashboardAccess();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  // Search functionality
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setSearchResults([]);
      setIsSearchOpen(false);
      return;
    }

    const lowerCaseSearch = searchTerm.toLowerCase().trim();
    const results = filteredModules.filter(
      (module) =>
        module.title.toLowerCase().includes(lowerCaseSearch) ||
        module.value.toLowerCase().includes(lowerCaseSearch) ||
        module.description?.toLowerCase().includes(lowerCaseSearch),
    );
    setSearchResults(results);
    setIsSearchOpen(true);
  }, [searchTerm, filteredModules]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        if (typeof document !== "undefined") {
          const searchInput = document.getElementById("searchInput");
          if (searchInput) {
            searchInput.focus();
          }
        }
      }
      if (e.key === "Escape") {
        handleSearchClear();
      }
    };

    if (typeof window !== "undefined") {
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, []);

  const handleSearchClear = () => {
    setSearchTerm("");
    setSearchResults([]);
    setIsSearchOpen(false);
  };

  // Theme Toggle Component
  const ThemeToggle = () => (
    <div className="relative">
      <button
        onClick={() => setIsThemeDropdownOpen(!isThemeDropdownOpen)}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 ${
          theme === "light"
            ? "bg-gray-200/60 hover:bg-gray-200/80 text-gray-700"
            : "bg-gray-700/30 hover:bg-gray-700/50 text-white"
        }`}
        aria-label="Toggle theme"
      >
        {theme === "dark" && <Moon className="w-4 h-4 text-yellow-400" />}
        {theme === "light" && <Sun className="w-4 h-4 text-yellow-500" />}
        {theme === "system" && <Monitor className="w-4 h-4 text-blue-400" />}
        <span className="text-sm font-medium">
          {theme === "dark" ? "Dark" : theme === "light" ? "Light" : "System"}
        </span>
      </button>

      {isThemeDropdownOpen && (
        <div
          className={`absolute right-0 mt-2 w-48 rounded-xl shadow-2xl border py-2 z-50 backdrop-blur-lg transition-all duration-300 ${
            theme === "light"
              ? "bg-white/95 border-gray-200/50 shadow-gray-200/30"
              : "bg-gray-800/95 border-gray-700/50 shadow-black/30"
          }`}
        >
          <button
            onClick={() => {
              setTheme("light");
              setIsThemeDropdownOpen(false);
            }}
            className={`w-full px-4 py-2.5 text-left text-sm transition-colors flex items-center gap-3 ${
              theme === "light"
                ? "text-gray-800 bg-gray-100/50"
                : "text-gray-300 hover:bg-gray-700/50"
            }`}
          >
            <Sun
              className={`w-4 h-4 ${theme === "light" ? "text-yellow-500" : "text-yellow-400"}`}
            />
            <span>Light</span>
            {theme === "light" && (
              <Check className="w-4 h-4 ml-auto text-blue-500" />
            )}
          </button>
          <button
            onClick={() => {
              setTheme("dark");
              setIsThemeDropdownOpen(false);
            }}
            className={`w-full px-4 py-2.5 text-left text-sm transition-colors flex items-center gap-3 ${
              theme === "dark"
                ? "text-white bg-gray-700/30"
                : "text-gray-300 hover:bg-gray-700/50"
            }`}
          >
            <Moon
              className={`w-4 h-4 ${theme === "dark" ? "text-yellow-400" : "text-gray-400"}`}
            />
            <span>Dark</span>
            {theme === "dark" && (
              <Check className="w-4 h-4 ml-auto text-blue-400" />
            )}
          </button>
          <button
            onClick={() => {
              setTheme("system");
              setIsThemeDropdownOpen(false);
            }}
            className={`w-full px-4 py-2.5 text-left text-sm transition-colors flex items-center gap-3 ${
              theme === "system"
                ? "text-white bg-gray-700/30"
                : "text-gray-300 hover:bg-gray-700/50"
            }`}
          >
            <Monitor
              className={`w-4 h-4 ${theme === "system" ? "text-blue-400" : "text-gray-400"}`}
            />
            <span>System Default</span>
            {theme === "system" && (
              <Check className="w-4 h-4 ml-auto text-blue-400" />
            )}
          </button>
        </div>
      )}
    </div>
  );

  const handleModuleClick = (route, title, value) => {
    if (value === "aifleetassistant") {
      setIsAIAssistantOpen(true);
      return;
    }
    const updated = [
      value,
      ...recentDashboards.filter((item) => item !== value),
    ].slice(0, 5);
    setRecentDashboards(updated);
    localStorage.setItem("recentDashboards", JSON.stringify(updated));

    window.open(route);
    localStorage.removeItem("status");
    setSearchTerm("");
    setSearchResults([]);
    setIsSearchOpen(false);
  };

  const toggleFavorite = (value, e) => {
    e.stopPropagation();
    const updated = favorites.includes(value)
      ? favorites.filter((v) => v !== value)
      : [...favorites, value];
    setFavorites(updated);
    localStorage.setItem("favoriteDashboards", JSON.stringify(updated));
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 17) return "Good Afternoon";
    return "Good Evening";
  };

  // Category configuration
  const categories = [
    {
      id: "sales",
      label: "SALES",
      value: "Sales",
      icon: categoryImages.sales,
      count: 0,
    },
    {
      id: "fleet",
      label: "FLEET",
      value: "Fleet",
      icon: categoryImages.fleet,
      count: 0,
    },
    {
      id: "booking",
      label: "BOOKING",
      value: "Booking",
      icon: categoryImages.booking,
      count: 0,
    },
    {
      id: "backend",
      label: "BACKEND",
      value: "Backend",
      icon: categoryImages.backend,
      count: 0,
    },
    {
      id: "accounts",
      label: "ACCOUNTS",
      value: "Accounts",
      icon: categoryImages.accounts,
      count: 0,
    },
    {
      id: "others",
      label: "OTHERS",
      value: "Others",
      icon: categoryImages.others,
      count: 0,
    },
    {
      id: "admin",
      label: "ADMIN",
      value: "Admin",
      icon: categoryImages.admin,
      count: 0,
    },
  ];

  // Get modules by category
  const getModulesByCategory = (categoryId) => {
    return filteredModules.filter((module) => module.category === categoryId);
  };

  // Update category counts
  categories.forEach((cat) => {
    cat.count = getModulesByCategory(cat.id).length;
  });

  // Get current category modules
  const currentModules = getModulesByCategory(activeCategory);

  if (isLoading) {
    return <Loading />;
  }

  const favoriteModules = filteredModules.filter((m) =>
    favorites.includes(m.value),
  );
  const recentModules = recentDashboards
    .map((value) => filteredModules.find((m) => m.value === value))
    .filter(Boolean);

  // Render search results
  const renderSearchResults = () => {
    if (!isSearchOpen || searchResults.length === 0) return null;

    return (
      <div
        className={`absolute top-full left-0 right-0 mt-2 rounded-xl shadow-2xl max-h-96 overflow-y-auto z-50 border ${
          theme === "light"
            ? "bg-gray-800 border-gray-200/50 shadow-gray-200/30"
            : "bg-gray-800/95 backdrop-blur-lg border-gray-700/50"
        }`}
      >
        <div className="p-2">
          {searchResults.map(
            ({ title, image, route, value, description, category }) => (
              <div
                key={value}
                onClick={() => handleModuleClick(route, title, value)}
                className={`flex items-center gap-4 p-3 rounded-lg cursor-pointer transition-all duration-200 group ${
                  theme === "light"
                    ? "hover:bg-gray-100/50"
                    : "hover:bg-gray-700/50"
                }`}
              >
                {/* <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 ring-1 ring-gray-600"> */}
                {/* <img
                    src={image}
                    alt={title}
                    className="w-full h-full object-cover"
                  /> */}
                {/* </div> */}
                <div className="flex-1 min-w-0">
                  <h3
                    className={`font-medium truncate ${
                      theme === "light" ? "text-white" : "text-white"
                    }`}
                  >
                    {title}
                  </h3>
                  {description && (
                    <p
                      className={`text-sm truncate ${
                        theme === "light" ? "text-white" : "text-gray-400"
                      }`}
                    >
                      {description}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-1">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full capitalize ${
                        theme === "light"
                          ? "bg-gray-200 text-gray-700"
                          : "bg-gray-700 text-gray-300"
                      }`}
                    >
                      {category}
                    </span>
                  </div>
                </div>
                <ArrowRight
                  className={`w-5 h-5 flex-shrink-0 transition-colors ${
                    theme === "light"
                      ? "text-gray-400 group-hover:text-gray-700"
                      : "text-gray-500 group-hover:text-white"
                  }`}
                />
              </div>
            ),
          )}
        </div>
      </div>
    );
  };

  // Render dashboard card
  const renderDashboardCard = ({
    title,
    image,
    route,
    value,
    description,
    category,
  }) => {
    const isFavorite = favorites.includes(value);

    return (
      <div
        key={value}
        onClick={() => handleModuleClick(route, title, value)}
        onMouseEnter={() => setHoveredModule(value)}
        onMouseLeave={() => setHoveredModule(null)}
        className="relative group cursor-pointer transform transition-all duration-300 hover:scale-105 hover:z-10"
      >
        <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl opacity-0 group-hover:opacity-100 transition duration-300 blur"></div>
        <div
          className={`relative rounded-xl overflow-hidden border transition-all duration-300 ${
            theme === "light"
              ? "bg-white/90 backdrop-blur-sm border-gray-200/50 hover:border-transparent"
              : "bg-gray-800/90 backdrop-blur-sm border-gray-700/50 hover:border-transparent"
          }`}
        >
          {/* <button
            onClick={(e) => toggleFavorite(value, e)}
            className="absolute top-2 right-2 z-10 p-1.5 bg-gray-900/80 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <Star
              className={`w-4 h-4 ${isFavorite ? "fill-yellow-400 text-yellow-400" : "text-gray-400 hover:text-yellow-400"} transition-colors`}
            />
          </button> */}

          <span
            // onClick={(e) => toggleFavorite(value, e)}
            className={`absolute text-xs top-2 capitalize right-2 z-10 p-1.5 bg-gray-900/80 rounded-lg hover:bg-gray-700 transition-colors ${
              theme === "light"
                ? "bg-gray-200 text-white"
                : "bg-gray-700 text-gray-300"
            }`}
          >
            {category}
          </span>

          <div className="relative overflow-hidden">
            <img
              src={image}
              alt={title}
              className="h-32 w-full object-contain p-2 transition-transform duration-500 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-transparent to-transparent"></div>
            {hoveredModule === value && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <span className="text-white text-sm font-medium px-4 py-2 bg-blue-600 rounded-lg">
                  Open Dashboard
                </span>
              </div>
            )}
          </div>

          <div className="p-3">
            <h3
              className={`font-medium text-sm truncate ${
                theme === "light" ? "text-gray-800" : "text-white"
              }`}
            >
              {title}
            </h3>
            {description && (
              <p
                className={`text-xs truncate mt-0.5 ${
                  theme === "light" ? "text-gray-600" : "text-gray-400"
                }`}
              >
                {description}
              </p>
            )}
            {/* <div className="flex items-center justify-between mt-2">
              <span
                className={`text-xs px-2 py-0.5 rounded-full capitalize ${
                  theme === "light"
                    ? "bg-gray-200 text-gray-700"
                    : "bg-gray-700 text-gray-300"
                }`}
              >
                {category}
              </span>
              <span
                className={`text-xs ${theme === "light" ? "text-gray-400" : "text-gray-500"}`}
              >
                →
              </span>
            </div> */}
          </div>
        </div>
      </div>
    );
  };

  // Render dashboard grid
  const renderDashboardGrid = (dashboards) => {
    if (dashboards.length === 0) {
      return (
        <div
          className={`text-center py-12 ${
            theme === "light" ? "text-gray-500" : "text-gray-400"
          }`}
        >
          <p>No dashboards available in this category.</p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
        {dashboards.map(renderDashboardCard)}
      </div>
    );
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto p-4 sm:p-6">
        <div className="flex gap-4 items-center justify-end">
          <ThemeToggle />
          <Topbar profile={profile} />
        </div>

        {isOnline ? (
          <>
            {/* Welcome Section with Stats */}
            <div
              className={`my-6 backdrop-blur-sm rounded-2xl p-6 border transition-all duration-300 ${
                theme === "light"
                  ? "bg-white/80 border-gray-200/50 shadow-lg shadow-gray-200/20"
                  : "bg-gray-800/40 border-gray-700/30 shadow-lg shadow-black/20"
              }`}
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h1
                    className={`text-3xl font-bold flex items-center gap-2 transition-colors duration-300 ${
                      theme === "light" ? "text-gray-900" : "text-white"
                    }`}
                  >
                    {getGreeting()}, {profile?.name || "User"}
                  </h1>
                  <p
                    className={`transition-colors duration-300 ${
                      theme === "light" ? "text-gray-700" : "text-gray-400"
                    }`}
                  >
                    Welcome to your dashboard. You have access to{" "}
                    {filteredModules.length} dashboards.
                  </p>
                </div>
                <div className="flex items-center gap-3 border border-2 border-gray-300 rounded-lg">
                  <div
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors duration-300 ${
                      theme === "light" ? "bg-gray-200/50" : "bg-gray-700/30"
                    }`}
                  >
                    <Clock
                      className={`w-4 h-4 ${
                        theme === "light" ? "text-blue-600" : "text-blue-400"
                      }`}
                    />
                    <span
                      className={`text-sm transition-colors duration-300 ${
                        theme === "light" ? "text-gray-700" : "text-white"
                      }`}
                    >
                      {new Date().toLocaleDateString("en-US", {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </span>
                  </div>
                  {isOnline && isSlowConnection && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-yellow-100 border border-yellow-300 rounded-full">
                      <div className="w-8 h-8 rounded-full bg-yellow-800 flex items-center justify-center animate-pulse">
                        <TriangleAlert className="w-5 h-5 text-yellow-400" />
                      </div>

                      <span className="text-sm font-medium text-yellow-900">
                        Slow Network
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-7 gap-2 my-4">
                {categories.map((cat) => {
                  const modulesCount = getModulesByCategory(cat.id).length;
                  if (modulesCount === 0) return null;

                  return (
                    <button
                      key={cat.id}
                      onClick={() => setActiveCategory(cat.id)}
                      className={`w-24 h-24 flex flex-col items-center justify-center p-4 rounded-full transition-all duration-300 hover:scale-110 hover:shadow-lg ${
                        activeCategory === cat.id
                          ? theme === "light"
                            ? "bg-blue-100 border-2 border-blue-500 shadow-lg shadow-blue-200/50"
                            : "bg-blue-900/40 border-2 border-blue-500 shadow-lg shadow-blue-900/30"
                          : theme === "light"
                            ? "bg-white/60 backdrop-blur-sm hover:bg-gray-100/60 border border-gray-200/50"
                            : "bg-gray-800/40 backdrop-blur-sm hover:bg-gray-700/50 border border-gray-700/30"
                      }`}
                    >
                      <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 mb-2">
                        <img
                          src={cat.icon}
                          alt={cat.label}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      </div>
                      <span
                        className={`text-xs font-medium ${
                          activeCategory === cat.id
                            ? theme === "light"
                              ? "text-blue-700"
                              : "text-blue-400"
                            : theme === "light"
                              ? "text-gray-700"
                              : "text-gray-300"
                        }`}
                      >
                        {cat.label}
                      </span>
                      <span
                        className={`text-lg mt-1 px-2 py-0.5 rounded-full ${
                          activeCategory === cat.id
                            ? theme === "light"
                              ? "bg-blue-200 text-blue-800"
                              : "bg-blue-700/50 text-blue-400"
                            : theme === "light"
                              ? "bg-gray-200 text-gray-800"
                              : "bg-gray-700 text-gray-400"
                        }`}
                      >
                        {modulesCount}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Search Bar */}
            <div className="relative mb-6">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Search
                    className={`h-5 w-5 ${theme === "light" ? "text-gray-500" : "text-gray-400"}`}
                  />
                </div>
                <input
                  id="searchInput"
                  type="text"
                  placeholder="Search dashboards... (Ctrl+K)"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full pl-12 pr-12 py-3.5 border rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${
                    theme === "light"
                      ? "bg-white/90 border-gray-300 text-gray-900 placeholder-gray-500"
                      : "bg-gray-800/60 border-gray-700/50 text-white placeholder-gray-400"
                  }`}
                />
                {searchTerm && (
                  <button
                    onClick={handleSearchClear}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center"
                  >
                    <X
                      className={`h-5 w-5 transition-colors ${
                        theme === "light"
                          ? "text-gray-500 hover:text-gray-700"
                          : "text-gray-400 hover:text-white"
                      }`}
                    />
                  </button>
                )}
              </div>
              {renderSearchResults()}
              {isSearchOpen &&
                searchResults.length === 0 &&
                searchTerm.trim() !== "" && (
                  <div
                    className={`absolute top-full left-0 right-0 mt-2 rounded-xl shadow-2xl p-6 border z-50 text-center ${
                      theme === "light"
                        ? "bg-white/95 border-gray-200/50 shadow-gray-200/30"
                        : "bg-gray-800/95 backdrop-blur-lg border-gray-700/50"
                    }`}
                  >
                    <p
                      className={
                        theme === "light" ? "text-gray-600" : "text-gray-400"
                      }
                    >
                      No dashboards found matching "{searchTerm}"
                    </p>
                  </div>
                )}
            </div>

            {/* Favorites Section */}
            {/* {favoriteModules.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
              <h2
                className={`text-xl font-semibold ${
                  theme === "light" ? "text-gray-800" : "text-white"
                }`}
              >
                Your Favorites
              </h2>
              <span
                className={`text-sm ${theme === "light" ? "text-gray-500" : "text-gray-400"}`}
              >
                ({favoriteModules.length})
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
              {favoriteModules.map(renderDashboardCard)}
            </div>
          </div>
        )} */}

            {/* Recent Dashboards
        {recentModules.length > 0 && !favoriteModules.some(m => recentModules.includes(m)) && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-blue-400" />
              <h2 className={`text-xl font-semibold ${
                theme === 'light' ? 'text-gray-800' : 'text-white'
              }`}>Recent</h2>
            </div>
            <div className="flex flex-wrap gap-2">
              {recentModules.map((module) => (
                <button
                  key={module.value}
                  onClick={() => handleModuleClick(module.route, module.title, module.value)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all border ${
                    theme === 'light'
                      ? 'bg-white/60 backdrop-blur-sm hover:bg-gray-100/60 text-gray-700 border-gray-200 hover:border-gray-300'
                      : 'bg-gray-800/60 backdrop-blur-sm hover:bg-gray-700/60 text-white border-gray-700/30 hover:border-gray-600'
                  }`}
                >
                  <img src={module.image} alt="" className="w-5 h-5 rounded object-cover" />
                  {module.title}
                </button>
              ))}
            </div>
          </div>
        )} */}

            {/* Category Title */}
            {/* <div className="flex items-center justify-between mb-4">
          <h2
            className={`text-xl font-semibold ${
              theme === "light" ? "text-gray-800" : "text-white"
            }`}
          >
            {categories.find((c) => c.id === activeCategory)?.value || ""}{" "}
            Dashboards
            <span
              className={`text-sm font-normal ml-2 ${
                theme === "light" ? "text-gray-500" : "text-gray-400"
              }`}
            >
              ({currentModules.length} dashboard
              {currentModules.length !== 1 ? "s" : ""})
            </span>
          </h2>

          <div
            className={`flex rounded-lg p-1 border ${
              theme === "light"
                ? "bg-white/60 backdrop-blur-sm border-gray-200/50"
                : "bg-gray-800/40 backdrop-blur-sm border-gray-700/30"
            }`}
          >
            <button
              onClick={() => setViewMode("grid")}
              className={`p-2 rounded-md transition-all ${
                viewMode === "grid"
                  ? "bg-blue-600 text-white"
                  : theme === "light"
                    ? "text-gray-600 hover:text-gray-800"
                    : "text-gray-400 hover:text-white"
              }`}
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`p-2 rounded-md transition-all ${
                viewMode === "list"
                  ? "bg-blue-600 text-white"
                  : theme === "light"
                    ? "text-gray-600 hover:text-gray-800"
                    : "text-gray-400 hover:text-white"
              }`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div> */}

            {/* Dashboard Grid for Selected Category */}
            {/* {viewMode === "grid" ? ( */}
            <div>{renderDashboardGrid(currentModules)}</div>
            {/* ) : ( */}
            {/* <div className="space-y-2">
            {currentModules.map(
              ({ title, image, route, value, description, category }) => (
                <div
                  key={value}
                  onClick={() => handleModuleClick(route, title, value)}
                  className={`flex items-center gap-4 p-3 rounded-lg cursor-pointer transition-all duration-200 border ${
                    theme === "light"
                      ? "bg-white/90 backdrop-blur-sm hover:bg-gray-100/80 border-gray-200/50 hover:border-gray-300"
                      : "bg-gray-800/90 backdrop-blur-sm hover:bg-gray-700/50 border-gray-700/50 hover:border-gray-600"
                  }`}
                >
                  <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={image}
                      alt={title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3
                      className={`font-medium ${
                        theme === "light" ? "text-gray-800" : "text-white"
                      }`}
                    >
                      {title}
                    </h3>
                    {description && (
                      <p
                        className={`text-sm truncate ${
                          theme === "light" ? "text-gray-600" : "text-gray-400"
                        }`}
                      >
                        {description}
                      </p>
                    )}
                  </div>
                  <span
                    className={`text-xs px-2 py-1 rounded-full capitalize ${
                      theme === "light"
                        ? "bg-gray-200 text-gray-700"
                        : "bg-gray-700 text-gray-300"
                    }`}
                  >
                    {category}
                  </span>
                  <ArrowRight
                    className={`w-4 h-4 ${theme === "light" ? "text-gray-400" : "text-gray-500"}`}
                  />
                </div>
              ),
            )}
            {currentModules.length === 0 && (
              <div
                className={`text-center py-12 ${
                  theme === "light" ? "text-gray-500" : "text-gray-400"
                }`}
              >
                <p>No dashboards available in this category.</p>
              </div>
            )}
          </div> */}
            {/* )} */}
          </>
        ) : (
          <>
            <NoInternet isOnline={isOnline} />
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
